import React, { useState, useEffect, Fragment, useMemo } from 'react';
import { matchPath, useLocation } from 'react-router-dom';

import useConsentManager from '../../../hooks/useConsentManager';
import Button from '../Button';
import { CONSENT_COOKIE_CATEGORIES } from '../../../constants/gdpr';
import useBasename from '../../routing/useBasename';
import { canAccessGDPROnBasename } from '../../../util/product-permissions';
import useGlobalToast from '../../../hooks/useGlobalToast';
import Emoji from '../Emoji';
import useIsNativeWebView from '../../../services/native-bridge/hooks/useIsWebView';

const CookieConsentBanner = () => {
  const location = useLocation();
  const basename = useBasename();
  const { setToast } = useGlobalToast();
  const [showBanner, setShowBanner] = useState(false);
  const { consent, loading, saveConsent } = useConsentManager();
  const isWebView = useIsNativeWebView();

  const IGNORED_PATHS = useMemo(
    () => [
      '/cards/print/:accountCardId/:side',
      '/marketing-materials/print/:accountMarketingMaterialId',
    ],
    [],
  );

  const handleReload = () => {
    setToast('Your preferences have been saved. Refreshing...');
    setTimeout(() => {
      window.location.reload();
    }, 1500);
  };

  useEffect(() => {
    // Check if user has already given consent
    // Consent is only relevant on the consumer side for now.
    const isIgnoredPath = !!IGNORED_PATHS.find(path =>
      matchPath(location.pathname, { path, exact: true }),
    );

    if (isIgnoredPath) {
      return;
    }

    if (
      !consent &&
      !loading &&
      (!basename || canAccessGDPROnBasename(basename))
    ) {
      setShowBanner(true);
    }
  }, [consent, loading, basename]);

  const handleAcceptAll = () => {
    saveConsent({
      [CONSENT_COOKIE_CATEGORIES.ESSENTIAL]: true,
      [CONSENT_COOKIE_CATEGORIES.NON_ESSENTIAL]: true,
    });
    setShowBanner(false);
    // Reload to initialize analytics
    handleReload();
  };

  const handleEssentialOnly = () => {
    saveConsent({
      [CONSENT_COOKIE_CATEGORIES.ESSENTIAL]: true,
      [CONSENT_COOKIE_CATEGORIES.NON_ESSENTIAL]: false,
    });
    setShowBanner(false);
    // Reload to initialize analytics
    handleReload();
  };

  if (!showBanner || isWebView) return null;

  return (
    <Fragment>
      <div className="cookie-consent-banner">
        <div className="cookie-consent-content">
          <p className="text-sm text-dark mb-2">
            <Emoji emoji="️️🍪" label="Cookie" /> We use cookies (sadly not the
            edible kind) to make our site work better, personalize your
            experience, and help us understand how you use it. Choose your level
            of indulgence below.
          </p>

          <div className="cookie-consent-actions">
            <Button
              btnSize="xs"
              className="text-extra-bold"
              onClick={handleEssentialOnly}
              btnStyle="link"
            >
              Only Necessary
            </Button>
            <Button btnSize="sm" onClick={handleAcceptAll} btnStyle="primary">
              Accept All
            </Button>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default CookieConsentBanner;
